import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../utils/api";
import DataTable from "../components/DataTable";
import { useAuth } from "../contexts/AuthContext";
import Modal from "../components/Modal";

let path;

const InvestmentDetails = () => {
  const { userId, fdNumber } = useParams();
  const [investment, setInvestment] = useState(null);
  const [modalAction, setModalAction] = useState(null); // "delete" | "markdone" | null
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const navigate = useNavigate();
  const { formatDate } = useAuth();

  useEffect(() => {
    const fetchInvestment = async () => {
      if (!fdNumber) return;
      path = userId
        ? `/admin/clients/${userId}/investments/${fdNumber}`
        : `/user/investments/${fdNumber}`;
      try {
        const res = await api.get(path);
        setInvestment(res.data.data);
      } catch (err) {
        console.error("Fetch investment failed:", err.response?.data || err);
      }
    };
    fetchInvestment();
  }, [userId, fdNumber]);

  // Add effect to prevent body scrolling when modals are open
  useEffect(() => {
    if (modalAction || isScreenshotModalOpen) {
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalAction, isScreenshotModalOpen]);

  const confirmInvestmentEdit = async () => {
    if (!modalAction) return;

    try {
      const res = await api.patch(
        `/admin/clients/${userId}/investments/${fdNumber}/status`,
        { status: modalAction }
      );
      if (res.data.success) {
        navigate(`/admin/clients/${userId}/investments`);
      } else {
        // console.log("Investment edit failed:", res.data.message);
      }
    } catch (err) {
      // console.log("Investment edit failed:", err.response?.data || err);
    } finally {
      setModalAction(null);
    }
  };

  const handleViewScreenshot = (screenshot) => {
    if (!screenshot) return;

    const url = screenshot.startsWith("http")
      ? screenshot
      : `${process.env.REACT_APP_API_URL}${screenshot}`;

    setScreenshots([url]);
    setSelectedScreenshotIndex(0);
    setIsScreenshotModalOpen(true);
  };

  const changeScreenshot = (direction) => {
    if (
      direction === "next" &&
      selectedScreenshotIndex < screenshots.length - 1
    ) {
      setSelectedScreenshotIndex(selectedScreenshotIndex + 1);
    } else if (direction === "prev" && selectedScreenshotIndex > 0) {
      setSelectedScreenshotIndex(selectedScreenshotIndex - 1);
    }
  };

  if (!investment) return <div className="text-center py-5">Loading...</div>;

  const breakdownColumns = [
    { key: "month", header: "Month" },
    {
      key: "date",
      header: "Date",
      render: (row) => formatDate(row.date),
    },
    {
      key: "interestAmt",
      header: "Interest Earned (₹)",
      render: (row) => `₹${row.interestAmt.toLocaleString("en-IN")}`,
    },
    {
      key: "value",
      header: "Closing Balance",
      render: (row) => `₹${row.value.toLocaleString("en-IN")}`,
    },
  ];

  const withdrawalColumns = [
    {
      key: "date",
      header: "Date",
      render: (row) => new Date(row.date).toLocaleDateString("en-IN"),
    },
    {
      key: "amount",
      header: "Amount Withdrawn",
      render: (row) => `₹${row.amount.toLocaleString("en-IN")}`,
    },
    {
      key: "paymentPlatform",
      header: "Platform",
      render: (row) => row.paymentPlatform || "N/A",
    },
    {
      key: "paymentScreenshot",
      header: "Proof",
      render: (row) =>
        row.paymentScreenshot ? (
          <button
            onClick={() => handleViewScreenshot(row.paymentScreenshot)}
            className="btn btn-sm btn-outline"
          >
            View Screenshot
          </button>
        ) : (
          "N/A"
        ),
    },
    {
      key: "previousValue",
      header: "Balance Before",
      render: (row) => `₹${row.previousValue.toLocaleString("en-IN")}`,
    },
    {
      key: "newValue",
      header: "Balance After",
      render: (row) => `₹${row.newValue.toLocaleString("en-IN")}`,
    },
    {
      key: "processedBy",
      header: "Processed By",
    },
  ];

  const remainingPrincipal =
    investment.amount -
    (investment.withdrawals?.reduce((sum, w) => sum + w.amount, 0) || 0);

  const totalInterestGains = investment.monthlyBreakdown.reduce(
    (sum, month) => sum + month.interestAmt,
    0
  );

  return (
    <div className="relative">
      <div className="card main-content">
        <div className="card-header flex justify-between items-center flex-wrap">
          <h2 className="card-title flex gap-2 mb-2">
            <span>Investment Details</span>
            <span
              className={`badge ${
                investment.status == "active"
                  ? "badge-primary"
                  : investment.status == "completed"
                  ? "badge-success"
                  : "badge-danger"
              }`}
            >
              {investment.status}
            </span>
          </h2>

          <div className="flex flex-col gap-2">
            {!path.split("/").includes("admin") && (
              <Link
                to={`/investments/${fdNumber}/withdraw`}
                className="btn btn-danger btn-sm"
              >
                Withdraw Money{" "}
              </Link>
            )}
            {path.split("/").includes("admin") && (
              <Link
                to={`/admin/clients/${userId}/investments/${fdNumber}/withdraw`}
                className="btn btn-danger btn-sm"
              >
                Withdraw Money{" "}
              </Link>
            )}
            {path.split("/").includes("admin") &&
              investment.status === "active" && (
                <Link
                  to={`/admin/clients/${userId}/investments/${fdNumber}/edit`}
                  className="btn btn-primary btn-sm"
                >
                  Edit Investment
                </Link>
              )}
          </div>
        </div>
        <div className="card-body">
          <div className="grid-cards mb-4">
            {/* Basic Details */}
            <div className="card mb-0">
              <div className="card-body">
                <div className="flex flex-col">
                  <div className="flex justify-between gap-4 items-center">
                    <strong>User:</strong>
                    <div className="flex items-center gap-1">
                      <img
                        src={
                          investment.userProfilePicture
                            ? investment.userProfilePicture
                            : "/default.png"
                        }
                        alt="Profile"
                        className="profile-pic smaller"
                      />
                      {investment.userName} ({investment.userId})
                    </div>
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>User Phone:</strong> {investment.userPhone}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>FD Number:</strong> {investment.fdNumber}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Start Date:</strong>{" "}
                    {formatDate(investment.startDate)}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Maturity Date:</strong>{" "}
                    {formatDate(investment.maturityDate)}
                  </div>
                  {/* Inside the Dates card */}
                  {investment.completedDate && (
                    <div className="flex justify-between gap-4 items-center">
                      <strong>Completed Date:</strong>{" "}
                      {formatDate(investment.completedDate)}
                    </div>
                  )}
                  {investment.deletedDate && (
                    <div className="flex justify-between gap-4 items-center">
                      <strong>Deleted Date:</strong>{" "}
                      {formatDate(investment.deletedDate)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Amount Details */}
            <div className="card mb-0">
              <div className="card-body">
                <div className="flex flex-col">
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Initial Principal Amount:</strong> ₹
                    {investment.amount.toLocaleString("en-IN")}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Interest Rate:</strong> {investment.interestRate}%
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Duration:</strong> {investment.duration} months
                  </div>
                </div>
              </div>
            </div>

            {/* Return Summary */}
            <div className="card bg-success-light mb-0">
              <div className="card-body">
                <div className="flex flex-col">
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Current Total Amount:</strong> ₹
                    {investment.currentValue.toLocaleString("en-IN")}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Current Principal:</strong> ₹
                    {remainingPrincipal.toLocaleString()}
                  </div>
                  <div className="flex justify-between gap-4 items-center">
                    <strong>Total Interest Gains after Maturity Date:</strong> ₹
                    {totalInterestGains.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <h3 className="card-title mb-2 mt-4">Monthly Breakdown</h3>
          <div className="table-container">
            <DataTable
              columns={breakdownColumns}
              data={investment.monthlyBreakdown}
              emptyMessage="No monthly data"
              className="table table-striped"
            />
          </div>

          {/* Admin Actions */}
          {path.split("/").includes("admin") &&
            investment.status === "active" && (
              <div className="flex gap-2 flex-wrap mt-4">
                <button
                  onClick={() => setModalAction("delete")}
                  className="btn btn-danger btn-sm"
                >
                  Delete Investment
                </button>
                <button
                  onClick={() => setModalAction("markdone")}
                  className="btn btn-success btn-sm"
                >
                  Mark Completed
                </button>
              </div>
            )}

          {/* Withdrawal History with DataTable */}
          {investment.withdrawals && investment.withdrawals.length > 0 && (
            <>
              <h3 className="card-title mb-2 mt-4">Withdrawal History</h3>
              <div className="table-container">
                <DataTable
                  columns={withdrawalColumns}
                  data={investment.withdrawals}
                  emptyMessage="No withdrawal history"
                  className="table table-striped"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal for delete/complete actions */}
      <Modal
        isOpen={modalAction !== null}
        onClose={() => setModalAction(null)}
        title={`Confirm ${modalAction === "delete" ? "Delete" : "Completion"}`}
        buttons={[
          {
            text: "Cancel",
            onClick: () => setModalAction(null),
            color: "outline",
          },
          {
            text: "Confirm",
            onClick: confirmInvestmentEdit,
            color: modalAction === "delete" ? "danger" : "success",
          },
        ]}
      >
        <p>
          Are you sure you want to{" "}
          {modalAction === "delete"
            ? "delete this investment"
            : "mark this investment as completed"}
          ?
        </p>
      </Modal>

      {/* Modal for viewing screenshots */}
      <Modal
        isOpen={isScreenshotModalOpen}
        onClose={() => setIsScreenshotModalOpen(false)}
        title="Payment Screenshot"
        size="md"
      >
        {screenshots.length > 0 ? (
          <>
            <div className="flex justify-center mb-4">
              <img
                src={screenshots[selectedScreenshotIndex]}
                alt="Payment Screenshot"
                className="max-w-full max-h-96 object-contain"
              />
            </div>
            {/* Screenshot navigation controls */}
            <div className="flex justify-between items-center mt-4">
              <div>
                Screenshot {selectedScreenshotIndex + 1} of {screenshots.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => changeScreenshot("prev")}
                  disabled={selectedScreenshotIndex === 0}
                  className="btn btn-sm btn-outline"
                >
                  Previous
                </button>
                <button
                  onClick={() => changeScreenshot("next")}
                  disabled={selectedScreenshotIndex === screenshots.length - 1}
                  className="btn btn-sm btn-outline"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4">No screenshots available.</div>
        )}
      </Modal>
    </div>
  );
};

export default InvestmentDetails;
